import type { ImageGenerationTask } from '../../../api'

/** Persisted on every node in a multi-candidate image generation batch. */
export const IMAGE_GENERATION_BATCH_ID_FIELD = 'imageGenerationBatchId'
export const IMAGE_GENERATION_BATCH_SLOT_FIELD = 'imageGenerationBatchSlotIndex'
export const IMAGE_GENERATION_BATCH_ORIGIN_FIELD = 'imageGenerationBatchOriginNodeId'
export const IMAGE_GENERATION_BATCH_REQUESTED_FIELD = 'imageGenerationBatchRequestedCount'
export const IMAGE_GENERATION_BATCH_COMMITTED_FIELD = 'imageGenerationBatchFanOutCommitted'
/** Non-origin siblings — skip interval polling; leader syncs task snapshots. */
export const IMAGE_GENERATION_BATCH_FOLLOWER_FIELD = 'imageGenerationBatchFollower'
/** Shared terminal error summary projected from canonical task (followers). */
export const IMAGE_GENERATION_BATCH_ERROR_SUMMARY_FIELD = 'imageGenerationBatchErrorSummary'

const PRE_SUBMIT_EXECUTION_STATUSES = new Set(['UPLOADING_ASSETS', 'SUBMITTING_REQUEST', 'SUBMITTING'])

export function imageGenerationBatchSlotIndex(fields: Record<string, unknown>): number {
  const raw = fields[IMAGE_GENERATION_BATCH_SLOT_FIELD]
  return typeof raw === 'number' && Number.isFinite(raw) ? Math.max(0, Math.floor(raw)) : 0
}

export function isImageGenerationBatchFollower(fields: Record<string, unknown>): boolean {
  return fields[IMAGE_GENERATION_BATCH_FOLLOWER_FIELD] === true
}

export function isImageGenerationBatchPollLeader(fields: Record<string, unknown>): boolean {
  if (!fields[IMAGE_GENERATION_BATCH_COMMITTED_FIELD]) return true
  return !isImageGenerationBatchFollower(fields)
}

// VistoeraTASK-00224 — a Canvas node that previously participated in a
// multi-candidate batch (as either the slot-0 origin/leader OR a
// fan-out follower/sibling) retains its batch-identity fields
// (`imageGenerationBatchFanOutCommitted`, `...Follower`, `...Id`,
// `...SlotIndex`, `...OriginNodeId`, `...RequestedCount`,
// `...ErrorSummary`) forever in `fields` — there was never a canonical
// "this node is starting an entirely new, independent Generate
// submission" reset. When the SAME node is later reused to submit a
// brand-new request (typing a new prompt / clicking "重新生成" after a
// failure / re-clicking Generate on a collapsed fan-out sibling), two
// real-Preview-observed failures followed from those stale fields:
//   1. `runTask()`'s own `candidateCount > 1 &&
//      !fields[IMAGE_GENERATION_BATCH_COMMITTED_FIELD]` fan-out guard
//      was permanently false, so `onCommitImageGenerationBatchFanOut`
//      never fired again — a fresh candidate_count=2 submission never
//      created a second Canvas node.
//   2. `isImageGenerationBatchPollLeader(fields)` was permanently false
//      (stale follower=true), so the node never scheduled a poll for
//      the BRAND NEW task_id it had just itself submitted — it has no
//      "leader" to rely on for that unrelated task, so it got stuck
//      showing a transient status forever while displaying a leftover
//      `imageGenerationBatchErrorSummary` string from the PRIOR,
//      unrelated batch's failure.
// Any node that itself calls `createImageGenerationTask` and gets a
// freshly-accepted task is, by definition, the origin/leader of that
// new request — regardless of what it may have been in a past batch.
// `runTask()`'s accept-handler must merge this reset into the very
// same `patchFields` call that records task acceptance, so the fields
// snapshot the fan-out committer (`CanvasBoard.tsx`'s
// `onCommitImageGenerationBatchFanOut`, reading `nodesRef.current`
// AFTER that patch lands) observes is already clean.
export function resetImageGenerationBatchFieldsPatch(): Record<string, unknown> {
  return {
    [IMAGE_GENERATION_BATCH_ID_FIELD]: undefined,
    [IMAGE_GENERATION_BATCH_SLOT_FIELD]: undefined,
    [IMAGE_GENERATION_BATCH_ORIGIN_FIELD]: undefined,
    [IMAGE_GENERATION_BATCH_REQUESTED_FIELD]: undefined,
    [IMAGE_GENERATION_BATCH_COMMITTED_FIELD]: false,
    [IMAGE_GENERATION_BATCH_FOLLOWER_FIELD]: false,
    [IMAGE_GENERATION_BATCH_ERROR_SUMMARY_FIELD]: null,
  }
}

export function pickImageGenerationOutputForSlot(
  task: ImageGenerationTask,
  slotIndex: number,
): ImageGenerationTask['outputs'][number] | undefined {
  const outputs = task.outputs || []
  const byIndex = outputs.find((o) => o.candidate_index === slotIndex)
  if (byIndex) return byIndex
  if (outputs.length === 1 && slotIndex === 0) return outputs[0]
  return undefined
}

export function requestedImageGenerationBatchCount(fields: Record<string, unknown>): number {
  const fromBatch = fields[IMAGE_GENERATION_BATCH_REQUESTED_FIELD]
  if (typeof fromBatch === 'number' && fromBatch >= 1) return Math.floor(fromBatch)
  const candidateCount = fields.candidateCount
  if (typeof candidateCount === 'number' && candidateCount >= 1) return Math.floor(candidateCount)
  return 1
}

function resolveBatchSlotExecutionFieldStatus(
  task: ImageGenerationTask,
  slotIndex: number,
  resolveFieldStatus: (task: ImageGenerationTask) => string,
): string {
  const raw = resolveFieldStatus(task)
  if (slotIndex > 0 && PRE_SUBMIT_EXECUTION_STATUSES.has(raw)) {
    return 'GENERATING'
  }
  return raw
}

function terminalUnresolvedSlotPatch(
  task: ImageGenerationTask,
): Record<string, unknown> {
  const base: Record<string, unknown> = {
    generatedOutputId: undefined,
    outputAssetId: null,
    previousOutputId: undefined,
    [IMAGE_GENERATION_BATCH_ERROR_SUMMARY_FIELD]: task.error_message || null,
  }
  if (task.status === 'interrupted') {
    return { ...base, status: 'INTERRUPTED' }
  }
  return { ...base, status: 'FAILED' }
}

// VistoeraTASK-00242 — exported so `onSplitGenerationOutputs` (the
// requested≠committed-count safety-net path, distinct from the
// accept-time `onCommitImageGenerationBatchFanOut` happy path) can
// stamp a newly-split sibling's real output identity (`generatedOutputId`
// + `outputAssetId` + native dimensions) instead of leaving it stale/
// inherited from whichever origin node's `fields` it was spread from.
export function completedSlotPatch(
  output: ImageGenerationTask['outputs'][number],
): Record<string, unknown> {
  return {
    status: 'COMPLETED',
    generatedOutputId: output.id,
    outputAssetId: output.site_asset_id || null,
    previousOutputId: undefined,
    provider_native_width: output.width ?? null,
    provider_native_height: output.height ?? null,
    [IMAGE_GENERATION_BATCH_ERROR_SUMMARY_FIELD]: null,
  }
}

/** Strip result pointers so a batch sibling starts as an independent generator card. */
export function buildImageGenerationBatchSiblingFields(
  originFields: Record<string, unknown>,
  meta: {
    batchId: string
    slotIndex: number
    originNodeId: string
    requestedCount: number
    taskId: string
  },
): Record<string, unknown> {
  const {
    generatedOutputId: _go,
    outputAssetId: _oa,
    previousOutputId: _po,
    generationHistory: _gh,
    status: _status,
    [IMAGE_GENERATION_BATCH_ERROR_SUMMARY_FIELD]: _err,
    ...inheritable
  } = originFields
  return {
    ...inheritable,
    generatedOutputId: undefined,
    outputAssetId: null,
    previousOutputId: undefined,
    generationHistory: Array.isArray(originFields.generationHistory) ? [...(originFields.generationHistory as unknown[])] : [],
    // Batch input-prep phases (reference upload, submit) are leader-only — never copy to slot carriers.
    status: 'GENERATING',
    [IMAGE_GENERATION_BATCH_ID_FIELD]: meta.batchId,
    [IMAGE_GENERATION_BATCH_SLOT_FIELD]: meta.slotIndex,
    [IMAGE_GENERATION_BATCH_ORIGIN_FIELD]: meta.originNodeId,
    [IMAGE_GENERATION_BATCH_REQUESTED_FIELD]: meta.requestedCount,
    [IMAGE_GENERATION_BATCH_COMMITTED_FIELD]: true,
    [IMAGE_GENERATION_BATCH_FOLLOWER_FIELD]: true,
    [IMAGE_GENERATION_BATCH_ERROR_SUMMARY_FIELD]: null,
    lastSubmittedGenerationParameters: originFields.lastSubmittedGenerationParameters ?? null,
  }
}

export function buildImageGenerationBatchOriginPatch(
  meta: {
    batchId: string
    originNodeId: string
    requestedCount: number
  },
): Record<string, unknown> {
  return {
    [IMAGE_GENERATION_BATCH_ID_FIELD]: meta.batchId,
    [IMAGE_GENERATION_BATCH_SLOT_FIELD]: 0,
    [IMAGE_GENERATION_BATCH_ORIGIN_FIELD]: meta.originNodeId,
    [IMAGE_GENERATION_BATCH_REQUESTED_FIELD]: meta.requestedCount,
    [IMAGE_GENERATION_BATCH_COMMITTED_FIELD]: true,
    [IMAGE_GENERATION_BATCH_FOLLOWER_FIELD]: false,
    [IMAGE_GENERATION_BATCH_ERROR_SUMMARY_FIELD]: null,
  }
}

export function batchSlotMissingOnTerminalTask(
  task: ImageGenerationTask,
  slotIndex: number,
  requestedCount: number,
): boolean {
  if (task.status !== 'succeeded') return false
  if (requestedCount <= 1) return false
  return pickImageGenerationOutputForSlot(task, slotIndex) == null
}

const IN_FLIGHT = new Set(['queued', 'running', 'interrupted'])

/** Maps a polled task snapshot onto one batch slot's canvas `fields` patch. */
export function buildImageGenerationSlotTaskFieldsPatch(
  task: ImageGenerationTask,
  slotIndex: number,
  requestedCount: number,
  resolveFieldStatus: (task: ImageGenerationTask) => string,
): Record<string, unknown> | null {
  // VistoeraTASK-00512 — a real output for this slot ALWAYS wins, even when
  // the slot index is beyond the originally requested count (the Provider
  // may return more valid images than requested; they must never be lost).
  const output = pickImageGenerationOutputForSlot(task, slotIndex)
  if (output) {
    return completedSlotPatch(output)
  }
  if (slotIndex >= requestedCount) {
    return { status: 'FAILED', generatedOutputId: undefined, outputAssetId: null }
  }

  if (task.status === 'failed' || task.status === 'interrupted') {
    return terminalUnresolvedSlotPatch(task)
  }

  if (task.status === 'succeeded') {
    const delivered = (task.outputs || []).length
    return {
      status: 'FAILED',
      generatedOutputId: undefined,
      outputAssetId: null,
      previousOutputId: undefined,
      [IMAGE_GENERATION_BATCH_ERROR_SUMMARY_FIELD]: `本次仅收到 ${delivered}/${requestedCount} 张图片，此位置未生成结果，可重新生成`,
    }
  }

  if (IN_FLIGHT.has(task.status)) {
    return { status: resolveBatchSlotExecutionFieldStatus(task, slotIndex, resolveFieldStatus) }
  }

  return { status: resolveBatchSlotExecutionFieldStatus(task, slotIndex, resolveFieldStatus) }
}

/** True when batch task is terminal but this slot has no artifact. */
export function isBatchSlotUnresolvedAtTerminal(
  task: ImageGenerationTask,
  slotIndex: number,
): boolean {
  if (task.status !== 'failed' && task.status !== 'interrupted' && task.status !== 'succeeded') {
    return false
  }
  return pickImageGenerationOutputForSlot(task, slotIndex) == null
}

// ---------------------------------------------------------------------------
// VistoeraTASK-00512 — Result Slot lifecycle (reconcile / lock / recovery).
// A "Result Slot" is only the Canvas CARRIER for one output of ONE generation
// run; it never decides whether the backend persists the output asset.
// ---------------------------------------------------------------------------

/** Every Canvas-field key that makes a node a member of a result group. */
export const IMAGE_GENERATION_BATCH_FIELD_KEYS = [
  IMAGE_GENERATION_BATCH_ID_FIELD,
  IMAGE_GENERATION_BATCH_SLOT_FIELD,
  IMAGE_GENERATION_BATCH_ORIGIN_FIELD,
  IMAGE_GENERATION_BATCH_REQUESTED_FIELD,
  IMAGE_GENERATION_BATCH_COMMITTED_FIELD,
  IMAGE_GENERATION_BATCH_FOLLOWER_FIELD,
  IMAGE_GENERATION_BATCH_ERROR_SUMMARY_FIELD,
] as const

/** Non-terminal slot statuses (includes the pre-submit phases). */
const RUNNING_SLOT_STATUSES = new Set([
  'UPLOADING_ASSETS',
  'SUBMITTING_REQUEST',
  'SUBMITTING',
  'RETRYING',
  'QUEUED',
  'GENERATING',
  'WAITING_FOR_ENGINE',
  'RUNNING',
  'PENDING',
])

export function isImageGenerationSlotStatusRunning(status: unknown): boolean {
  return typeof status === 'string' && RUNNING_SLOT_STATUSES.has(status)
}

/** A committed result-group member whose run has not reached a terminal state. */
export function isImageGenerationResultSlotRunning(fields: Record<string, unknown> | null | undefined): boolean {
  if (!fields) return false
  if (fields[IMAGE_GENERATION_BATCH_COMMITTED_FIELD] !== true) return false
  return isImageGenerationSlotStatusRunning(fields.status)
}

export interface DeleteLockNodeLike {
  id: string
  data?: unknown
}

/** Splits delete targets into deletable ids and ids locked by a running run. */
export function partitionImageGenerationSlotDeleteLock(
  nodes: DeleteLockNodeLike[],
  ids: string[],
): { allowed: string[]; locked: string[] } {
  const byId = new Map(nodes.map((n) => [n.id, n]))
  const allowed: string[] = []
  const locked: string[] = []
  for (const id of ids) {
    const data = byId.get(id)?.data as { nodeType?: string; fields?: Record<string, unknown> } | undefined
    if (data?.nodeType === 'generate_image' && isImageGenerationResultSlotRunning(data.fields)) locked.push(id)
    else allowed.push(id)
  }
  return { allowed, locked }
}

export interface ResultSlotPlan {
  /** Existing members kept in the group, with their NEW slot index (1..N-1). */
  kept: Array<{ id: string; slotIndex: number }>
  /** Existing members beyond the target count — detached, never deleted. */
  detachedIds: string[]
  /** Slot indexes that need a freshly created empty Result Slot. */
  createSlotIndexes: number[]
}

/**
 * Pure planner for "overwrite regenerate": the clicked node becomes slot 0 of
 * a NEW run; existing group members are reused in their stable order; missing
 * slots are created so exactly `requestedCount` slots exist before submit.
 */
export function planResultSlotReconciliation(args: {
  members: Array<{ id: string; slotIndex: number }>
  requestedCount: number
}): ResultSlotPlan {
  const target = Math.max(1, Math.floor(args.requestedCount))
  const ordered = [...args.members].sort((a, b) => a.slotIndex - b.slotIndex || a.id.localeCompare(b.id))
  const kept = ordered.slice(0, target - 1).map((m, i) => ({ id: m.id, slotIndex: i + 1 }))
  const detachedIds = ordered.slice(target - 1).map((m) => m.id)
  const createSlotIndexes: number[] = []
  for (let slot = kept.length + 1; slot <= target - 1; slot++) createSlotIndexes.push(slot)
  return { kept, detachedIds, createSlotIndexes }
}

/**
 * Outputs of a SUCCEEDED run that have no Canvas carrier yet (slot deleted by
 * a legacy bug, or the Provider returned more than requested). Pure; the
 * caller must additionally gate on "this run was observed in flight".
 */
export function outputsNeedingResultSlots(
  task: ImageGenerationTask,
  occupiedSlotIndexes: Set<number>,
  alreadyMaterializedOutputIds: Set<string>,
): Array<ImageGenerationTask['outputs'][number]> {
  if (task.status !== 'succeeded') return []
  return (task.outputs || [])
    .filter((o) => !occupiedSlotIndexes.has(o.candidate_index) && !alreadyMaterializedOutputIds.has(o.id))
    .sort((a, b) => a.candidate_index - b.candidate_index)
}
